import React, { useState, useEffect } from 'react';
import { 
  LuTent, 
  LuMapPin, 
  LuUser, 
  LuPhone, 
  LuMail, 
  LuCircleCheck, 
  LuLoader,
  LuMessageCircle,
  LuSparkles
} from 'react-icons/lu';
import { GiChurch } from 'react-icons/gi';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../shared/firebase';

export default function RetreatCenter() {
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function fetchRetreatCenters() {
      try {
        setLoading(true);
        let querySnapshot;
        try {
          querySnapshot = await getDocs(collection(db, 'retreat-center'));
          if (querySnapshot.empty) {
            querySnapshot = await getDocs(collection(db, 'retreat_center'));
          }
          if (querySnapshot.empty) {
            querySnapshot = await getDocs(collection(db, 'retreatcenters'));
          }
        } catch (e) {
          try {
            querySnapshot = await getDocs(collection(db, 'retreat_center'));
          } catch (e2) {
            querySnapshot = await getDocs(collection(db, 'retreat-center'));
          }
        }

        const fetched = [];
        if (querySnapshot && !querySnapshot.empty) {
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            const name = data.retreatCenterName || data.name || data.title || "CSI Retreat Center";
            const address = data.address || data.location || "";
            const email = data.email || "";
            const order = typeof data.order === 'number' ? data.order : 999;

            // Process Chairman
            const chairmanName = data.chairmanName && data.chairmanName !== "." ? data.chairmanName : "";
            const chairmanMob = data.chairmanMobile && data.chairmanMobile !== "0" ? data.chairmanMobile : "";

            // Process Caretaker
            const caretakerName = data.careTakerName && data.careTakerName !== "." ? data.careTakerName : "";
            const caretakerMob = data.careTakerNumber && data.careTakerNumber !== "0" ? data.careTakerNumber : "";

            // Process Incharge
            const inchargeName = data.inchargeName && data.inchargeName !== "." ? data.inchargeName : "";
            const inchargeMob = data.inchargeMobile && data.inchargeMobile !== "0" ? data.inchargeMobile : "";

            // Process President
            const presidentName = data.presidentName && data.presidentName !== "." ? data.presidentName : "";
            const presidentMob = data.presidentMobile && data.presidentMobile !== "0" ? data.presidentMobile : "";

            // Process facilities array or string
            let facilities = [];
            if (Array.isArray(data.facilities)) {
              facilities = data.facilities;
            } else if (typeof data.facilities === 'string' && data.facilities.trim()) {
              facilities = data.facilities.split('\n').filter(Boolean);
            }

            fetched.push({
              id: doc.id,
              name,
              address,
              email,
              chairman: { name: chairmanName, mob: chairmanMob },
              caretaker: { name: caretakerName, mob: caretakerMob },
              incharge: { name: inchargeName, mob: inchargeMob },
              president: { name: presidentName, mob: presidentMob },
              facilities,
              order,
              raw: data
            });
          });

          fetched.sort((a, b) => a.order - b.order);
        }

        if (isMounted) {
          setCenters(fetched);
        }
      } catch (err) {
        console.error("Error fetching retreat centers from Firebase:", err);
        if (isMounted) setCenters([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchRetreatCenters();
    return () => { isMounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#5F5A58] font-sans flex flex-col pb-16">
      
      {/* Page Header */}
      <section className="relative bg-gradient-to-b from-[#FAF6F2] via-[#F5EFEE] to-[#E8DDD7] pt-28 sm:pt-32 pb-16 overflow-hidden border-b border-[#E8DDD7]">
        <div className="absolute right-[5%] top-1/2 -translate-y-1/2 pointer-events-none opacity-[0.03] select-none">
          <GiChurch className="w-64 h-64 sm:w-96 sm:h-96 text-[#7B1E3A]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-12 flex flex-col items-center md:items-start text-center md:text-left">
          <div className="w-16 h-1 bg-[#D4A64A] mb-6 rounded-full" />
          <h1 className="font-serif font-bold text-[#7B1E3A] text-[38px] sm:text-[50px] lg:text-[56px] uppercase tracking-[0.03em] leading-[1.05]">
            Retreat Centers
          </h1>
          <p className="mt-4 max-w-xl text-[#7B6B66] text-[16px] sm:text-[17px] leading-[1.6]">
            A sanctuary of prayer, spiritual stillness, communion, and refreshment for the faithful, youth groups, and families.
          </p>
        </div>
      </section>

      {/* Main Content Centers List */}
      <div className="max-w-[1440px] mx-auto px-6 sm:px-8 mt-12 w-full flex-grow space-y-10">
        
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[24px] border border-gray-100 shadow-sm">
            <LuLoader className="w-10 h-10 text-[#842343] animate-spin mb-3" />
            <p className="text-sm font-semibold text-[#5A4F4A]">Connecting to Firebase & loading retreat centers...</p>
          </div>
        ) : (
          centers.map((center, idx) => {
            const cleanCaretakerPhone = center.caretaker?.mob ? center.caretaker.mob.replace(/\D/g, '') : '';

            return (
              <div
                key={center.id || idx}
                className="bg-white rounded-[32px] border border-[#E8DDD7] shadow-[0_10px_35px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_50px_rgba(123,30,58,0.08)] p-8 sm:p-10 lg:p-12 relative overflow-hidden group transition-all"
              >
                {/* Decorative background element */}
                <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-br from-[#FAF6F2] via-[#F8EFEA] to-transparent rounded-bl-full opacity-60 pointer-events-none transition-transform duration-500 group-hover:scale-110" />

                <div className="relative z-10">
                  
                  {/* Center Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5 mb-8 pb-6 border-b border-[#F0EBE6]">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-2xl bg-[#842343] text-white flex items-center justify-center shadow-md shrink-0">
                        <LuTent className="w-8 h-8" />
                      </div>
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#842343] bg-[#842343]/10 px-2.5 py-0.5 rounded-full inline-block mb-1">
                          CSI Diocese of Malabar
                        </span>
                        <h2 className="font-serif font-bold text-[#842343] text-2xl sm:text-3xl leading-snug">
                          {center.name}
                        </h2>
                      </div>
                    </div>

                    {center.email && (
                      <a
                        href={`mailto:${center.email}`}
                        className="inline-flex items-center gap-2 text-xs font-semibold px-4 py-2.5 rounded-xl bg-[#FAF7F2] border border-[#E8DDD7] text-[#842343] hover:bg-[#842343] hover:text-white transition-colors"
                      >
                        <LuMail className="w-3.5 h-3.5" />
                        <span>{center.email}</span>
                      </a>
                    )}
                  </div>

                  {/* 2-Column Grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    
                    {/* Left: Leadership & Contact Details (5 cols) */}
                    <div className="lg:col-span-5 space-y-4">
                      
                      {/* Chairman */}
                      {center.chairman?.name && (
                        <div className="p-4 rounded-2xl bg-[#FAF7F2] border border-[#E8DDD7] flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3.5">
                            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-[#842343] shrink-0 border border-[#E8DDD7] shadow-2xs">
                              <GiChurch className="w-5 h-5 text-[#D4A64A]" />
                            </div>
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-[#8A7E79]">Chairman</p>
                              <p className="text-[#1a1715] font-serif font-bold text-base mt-0.5">{center.chairman.name}</p>
                              {center.chairman.mob && (
                                <a href={`tel:${center.chairman.mob}`} className="text-xs font-bold text-[#842343] hover:underline mt-0.5 block">
                                  {center.chairman.mob}
                                </a>
                              )}
                            </div>
                          </div>

                          {center.chairman.mob && (
                            <a
                              href={`tel:${center.chairman.mob}`}
                              className="p-2 rounded-xl bg-white text-[#842343] border border-[#E8DDD7] hover:bg-[#842343] hover:text-white transition-colors shrink-0 shadow-2xs"
                              title="Call Chairman"
                            >
                              <LuPhone className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      )}

                      {/* Caretaker / Resident In-Charge */}
                      {center.caretaker?.name && (
                        <div className="p-4 rounded-2xl bg-[#FAF7F2] border border-[#E8DDD7] flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3.5">
                            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-[#842343] shrink-0 border border-[#E8DDD7] shadow-2xs">
                              <LuUser className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-[#8A7E79]">Care Taker / Resident In Charge</p>
                              <p className="text-[#1a1715] font-serif font-bold text-base mt-0.5">{center.caretaker.name}</p>
                              {center.caretaker.mob && (
                                <a href={`tel:${center.caretaker.mob}`} className="text-xs font-bold text-[#842343] hover:underline mt-0.5 block">
                                  {center.caretaker.mob}
                                </a>
                              )}
                            </div>
                          </div>

                          {center.caretaker.mob && (
                            <div className="flex items-center gap-1.5 shrink-0">
                              <a
                                href={`tel:${center.caretaker.mob}`}
                                className="p-2 rounded-xl bg-[#842343] text-white hover:bg-[#6c1b35] transition-colors shadow-2xs"
                                title="Call Caretaker"
                              >
                                <LuPhone className="w-3.5 h-3.5" />
                              </a>
                              {cleanCaretakerPhone && (
                                <a
                                  href={`https://wa.me/91${cleanCaretakerPhone}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="p-2 rounded-xl bg-[#25D366] text-white hover:bg-[#20bd5a] transition-colors shadow-2xs"
                                  title="WhatsApp Caretaker"
                                >
                                  <LuMessageCircle className="w-3.5 h-3.5" />
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Address */}
                      {center.address && (
                        <div className="p-4 rounded-2xl bg-[#FAF7F2] border border-[#E8DDD7] flex items-start gap-3.5">
                          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-[#842343] shrink-0 border border-[#E8DDD7] shadow-2xs">
                            <LuMapPin className="w-5 h-5 text-[#D4A64A]" />
                          </div>
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-[#8A7E79]">Location &amp; Address</p>
                            <p className="text-[#1a1715] font-medium text-sm mt-0.5 leading-relaxed">{center.address}</p>
                          </div>
                        </div>
                      )}

                    </div>

                    {/* Right: Facilities & Accommodation Offerings (7 cols) */}
                    <div className="lg:col-span-7 bg-[#FAF7F2] rounded-[24px] p-6 sm:p-7 border border-[#E8DDD7] shadow-2xs">
                      <div className="flex items-center justify-between gap-3 mb-5 pb-3 border-b border-[#E8DDD7]/70">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-[#842343] text-white flex items-center justify-center shadow-xs">
                            <LuCircleCheck className="w-4 h-4" />
                          </div>
                          <h3 className="font-serif font-bold text-[#1a1715] text-lg">
                            Facilities &amp; Amenities
                          </h3>
                        </div>
                        <span className="text-xs text-[#8A7E79] font-semibold">
                          {center.facilities.length} Offerings
                        </span>
                      </div>

                      <ul className="space-y-3">
                        {center.facilities.map((facility, fIdx) => (
                          <li
                            key={fIdx}
                            className="flex items-start gap-3.5 bg-white p-3.5 rounded-xl border border-[#E8DDD7]/80 shadow-2xs"
                          >
                            <div className="w-2 h-2 rounded-full bg-[#842343] mt-2 shrink-0" />
                            <span className="text-xs sm:text-[13.5px] font-semibold text-[#3D332E] leading-relaxed">
                              {facility}
                            </span>
                          </li>
                        ))}
                      </ul>

                      <div className="mt-6 pt-4 border-t border-[#E8DDD7]/70 flex items-center gap-2 text-xs text-[#8A7E79]">
                        <LuSparkles className="w-4 h-4 text-[#D4A64A] shrink-0" />
                        <span>For group bookings, prayer retreats, and convention stays, please contact the caretaker or chairman directly.</span>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            );
          })
        )}

      </div>
    </div>
  );
}
